<script setup>
import { watch, onMounted, onBeforeUnmount } from 'vue';

// eslint-disable-next-line no-undef
const props = defineProps({
    /**
     * Size of the loader in pixels
     * @default 40
     */
    size: {
        type: Number,
        default: 40,
    },
    /**
     * Color of the loader
     * @default #FF0090 (primary color)
     */
    color: {
        type: String,
        default: '#FF0090',
    },
    /**
     * Thickness of the loader ring
     * @default 4
     */
    thickness: {
        type: Number,
        default: 4,
    },
    /**
     * Whether to show a full-page overlay backdrop.
     * @default true
     */
    fullPage: {
        type: Boolean,
        default: true,
    },
    /**
     * Optional text to display below the loader
     */
    text: {
        type: String,
        default: '',
    },
    /**
     * Whether to prevent scrolling of the background when loader is active
     * @default true
     */
    preventScroll: {
        type: Boolean,
        default: true,
    },
});

// Function to toggle the prevent-scroll class on the body
const toggleBodyScroll = (prevent) => {
    if (prevent) {
        document.body.classList.add('prevent-scroll');
    } else {
        document.body.classList.remove('prevent-scroll');
    }
};

// Watch for changes in the preventScroll prop
watch(() => props.preventScroll, (newValue) => {
    toggleBodyScroll(newValue);
});

// Add the class when the component is mounted if preventScroll is true
onMounted(() => {
    if (props.preventScroll) {
        toggleBodyScroll(true);
    }
});

// Remove the class when the component is unmounted
onBeforeUnmount(() => {
    toggleBodyScroll(false);
});
</script>

<template>
    <teleport to="body">
        <div
            :class="[
                'loader-container',
                { 'full-page-overlay': props.fullPage },
                { 'prevent-scroll': props.preventScroll }
            ]"
        >
            <div
                class="spinner"
                :style="{
                    width: props.size + 'px',
                    height: props.size + 'px',
                    borderWidth: props.thickness + 'px',
                    borderTopColor: props.color
                }"
            ></div>
            <p v-if="props.text" class="loader-text">{{ props.text }}</p>
        </div>
    </teleport>
</template>

<style scoped>
.loader-container {
    @apply tw-flex tw-flex-col tw-items-center tw-justify-center;
}

.full-page-overlay {
    @apply tw-fixed tw-inset-0 tw-bg-black/30 tw-z-50 tw-backdrop-blur-sm;
}

.prevent-scroll {
    @apply tw-overflow-hidden;
}

/* When prevent-scroll is active, apply these styles to the body */
:global(body.prevent-scroll) {
    @apply tw-overflow-hidden tw-h-full;
    /* Fixed position to prevent scrolling */
    position: fixed;
    width: 100%;
}

.spinner {
    @apply tw-rounded-full tw-animate-spin;
    border-style: solid;
    border-color: #E5E7EB;
    border-top-style: solid;
}

.loader-text {
    @apply tw-mt-4 tw-text-[1.2em] tw-font-semibold tw-text-white;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>
